import React, { useState } from "react";
import { ArrowRight, ShieldCheck, Loader2 } from "lucide-react";

export default function BookDemoForm({ onSuccess }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    jobTitle: "",
  });

  const [status, setStatus] = useState("idle"); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.company) {
      setErrorMessage("Please fill in all required fields marked with *");
      return;
    }


    setStatus("submitting");

    // Simulate clean async submission
    setTimeout(() => {
      setStatus("success");
      if (onSuccess) {
        onSuccess(formData);
      }
    }, 600);
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center justify-center text-center p-8 bg-white rounded-2xl border border-purple-200 shadow-xl min-h-[460px]">
        <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center text-[#7c3aed] mb-6">
          <ShieldCheck size={36} />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-3">Demo Requested!</h3>
        <p className="text-slate-600 max-w-md mb-6 leading-relaxed text-sm">
          Thank you, <strong className="text-slate-900">{formData.firstName}</strong>. Our ThreatForge security team has received your request and will reach out to <span className="text-[#7c3aed] font-medium">{formData.email}</span> within 24 hours to schedule your personalized session.
        </p>
        <div className="p-4 bg-slate-50 rounded-xl text-xs text-slate-500 mb-8 max-w-sm border border-slate-200">
          <span className="font-semibold text-slate-700 block mb-1">What happens next?</span>
          You will receive an invitation with live access to ThreatForge Sentry 2.0 AI and a custom Secure Design Graph demo tailored for {formData.company || "your organization"}.
        </div>
        <button
          onClick={() => {
            setStatus("idle");
            setFormData({
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              company: "",
              jobTitle: "",
            });
          }}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#7c3aed] text-white font-semibold text-sm hover:bg-[#6d28d9] transition-all shadow-md"
        >
          <span>Book Another Demo</span>
          <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="tfl-demo-form-card relative">
      <div className="mb-8">
        <h2 className="tfl-demo-form-title">
          Schedule a demo with our team
        </h2>
        <p className="tfl-demo-form-desc mt-3">
          See how ThreatForge connects developers and security teams to governed, repeatable threat modeling built for AI-speed development.
        </p>
      </div>

      {errorMessage && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name Pair */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First Name"
              required
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent transition-all placeholder:text-slate-400"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Last Name"
              required
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent transition-all placeholder:text-slate-400"
            />
          </div>
        </div>

        {/* Email & Phone Pair */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Work Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="name@company.com"
              required
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent transition-all placeholder:text-slate-400"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 (555) 000-0000"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent transition-all placeholder:text-slate-400"
            />
          </div>
        </div>

        {/* Company & Job Title Pair */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="company" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Company <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Company Name"
              required
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent transition-all placeholder:text-slate-400"
            />
          </div>
          <div>
            <label htmlFor="jobTitle" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Job Title
            </label>
            <input
              type="text"
              id="jobTitle"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              placeholder="e.g. Head of AppSec"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent transition-all placeholder:text-slate-400"
            />
          </div>
        </div>


        {/* Submit Button */}
        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full mt-4 py-3.5 px-6 rounded-lg bg-[#7c3aed] hover:bg-[#6d28d9] text-white font-semibold text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-600/20 disabled:opacity-70"
        >
          {status === "submitting" ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>Scheduling Demo...</span>
            </>
          ) : (
            <>
              <span>Book a Demo</span>
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </form>
    </div>
  );
}
